import { useCallback } from 'react';

/**
 * Hook para interactuar con Google Ads (gtag.js)
 * 
 * @returns {Object} Funciones para tracking de eventos y conversiones
 */
const useGoogleAds = () => {
    /**
     * Función helper para verificar si gtag está disponible
     */
    const isGtagAvailable = () => {
        return typeof window !== 'undefined' && typeof window.gtag === 'function';
    };

    /**
     * Enviar evento personalizado a Google Ads
     * 
     * @param {string} eventName - Nombre del evento
     * @param {Object} parameters - Parámetros adicionales del evento
     * 
     * @example
     * trackEvent('generate_lead', {
     *   event_category: 'engagement',
     *   event_label: 'contact_form',
     *   value: 1
     * });
     */
    const trackEvent = useCallback((eventName, parameters = {}) => {
        if (isGtagAvailable()) {
            window.gtag('event', eventName, parameters);
            console.log(`[Google Ads] Event tracked: ${eventName}`, parameters);
        } else {
            console.warn('[Google Ads] gtag not available');
        }
    }, []);

    /**
     * Enviar conversión a Google Ads
     * 
     * @param {string} conversionLabel - Label de conversión de Google Ads
     * @param {Object} options - Opciones adicionales (value, currency, etc.)
     * 
     * @example
     * trackConversion('ABC123xyz', {
     *   value: 100.0,
     *   currency: 'EUR',
     *   transaction_id: 'ORDER_123'
     * });
     */
    const trackConversion = useCallback((conversionLabel, options = {}) => {
        if (isGtagAvailable()) {
            window.gtag('event', 'conversion', {
                'send_to': `AW-17655622399/${conversionLabel}`,
                ...options
            });
            console.log(`[Google Ads] Conversion tracked: ${conversionLabel}`, options);
        } else {
            console.warn('[Google Ads] gtag not available');
        }
    }, []);

    /**
     * Enviar evento de generación de lead
     * 
     * @param {string} source - Fuente del lead (ej: 'contact_form', 'whatsapp', 'phone')
     * @param {Object} options - Opciones adicionales
     * 
     * @example
     * trackLead('whatsapp_click', {
     *   page: '/reformas-landing',
     *   location: 'ripollet'
     * });
     */
    const trackLead = useCallback((source, options = {}) => {
        trackEvent('generate_lead', {
            event_category: 'lead_generation',
            event_label: source,
            ...options
        });
    }, [trackEvent]);

    /**
     * Enviar evento de click en CTA
     * 
     * @param {string} ctaName - Nombre del CTA
     * @param {string} destination - Destino del CTA
     * @param {Object} options - Opciones adicionales
     * 
     * @example
     * trackCTAClick('hero_cta', 'whatsapp', {
     *   page: '/reformas-landing'
     * });
     */
    const trackCTAClick = useCallback((ctaName, destination, options = {}) => {
        trackEvent('cta_click', {
            event_category: 'engagement',
            event_label: `${ctaName}_${destination}`,
            cta_name: ctaName,
            cta_destination: destination,
            ...options
        });
    }, [trackEvent]);

    /**
     * Enviar evento de vista de página personalizado
     * Útil para SPAs donde gtag.config no detecta cambios de ruta
     * 
     * @param {string} path - Ruta de la página
     * @param {string} title - Título de la página
     * 
     * @example
     * trackPageView('/reformas-landing', 'Reformas Landing');
     */
    const trackPageView = useCallback((path, title) => {
        if (isGtagAvailable()) {
            window.gtag('config', 'AW-17655622399', {
                page_path: path,
                page_title: title
            });
            console.log(`[Google Ads] Page view tracked: ${path}`);
        }
    }, []);

    /**
     * Configurar información del usuario
     * 
     * @param {Object} userData - Datos del usuario
     * 
     * @example
     * setUserData({
     *   user_id: '12345',
     *   crm_id: 'CRM_12345'
     * });
     */
    const setUserData = useCallback((userData) => {
        if (isGtagAvailable()) {
            window.gtag('set', 'user_data', userData);
            console.log('[Google Ads] User data set', userData);
        }
    }, []);

    return {
        trackEvent,
        trackConversion,
        trackLead,
        trackCTAClick,
        trackPageView,
        setUserData,
        isGtagAvailable
    };
};

export default useGoogleAds;

